import React, { createContext, useState, useContext, useEffect } from 'react';
import authApi from '../services/authApi';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuthStatus = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const response = await authApi.getCurrentUser();
          setCurrentUser(response.data);
        } catch (err) {
          console.error('Failed to fetch user data:', err);
          // Token might be invalid, but refresh token interceptor will handle this
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (credentials) => {
    setError(null);
    try {
      const response = await authApi.login(credentials);
      const { access, refresh, user } = response.data;
      
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);
      
      // If user data is returned with the token, use it
      if (user) {
        setCurrentUser(user);
      } else {
        // Otherwise fetch user data
        const userResponse = await authApi.getCurrentUser();
        setCurrentUser(userResponse.data);
      }
      
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.non_field_errors?.[0] || 
                          'Authentication failed';
      setError(errorMessage);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setCurrentUser(null);
    }
  };

  const updateUserProfile = async (userData) => {
    try {
      const response = await authApi.updateUserProfile(userData);
      setCurrentUser({...currentUser, ...response.data});
      return response.data;
    } catch (err) {
      const errorMessage = Object.values(err.response?.data || {})
        .flat()
        .join(', ');
      setError(errorMessage || 'Failed to update profile');
      throw err;
    }
  };

  const updatePassword = async (passwordData) => {
    try {
      await authApi.updatePassword(passwordData);
      return true;
    } catch (err) {
      const errorMessage = Object.values(err.response?.data || {})
        .flat()
        .join(', ');
      setError(errorMessage || 'Failed to update password');
      throw err;
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      await authApi.requestPasswordReset(email);
      return true;
    } catch (err) {
      const errorMessage = Object.values(err.response?.data || {})
        .flat()
        .join(', ');
      setError(errorMessage || 'Failed to send reset email');
      throw err;
    }
  };

  const confirmPasswordReset = async (data) => {
    try {
      await authApi.confirmPasswordReset(data);
      return true;
    } catch (err) {
      const errorMessage = Object.values(err.response?.data || {})
        .flat()
        .join(', ');
      setError(errorMessage || 'Failed to reset password');
      throw err;
    }
  };

  const register = async (userData) => {
    try {
      await authApi.register(userData);
      return true;
    } catch (err) {
      const errorMessage = Object.values(err.response?.data || {})
        .flat()
        .join(', ');
      setError(errorMessage || 'Registration failed');
      throw err;
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    updateUserProfile,
    updatePassword,
    requestPasswordReset,
    confirmPasswordReset,
    register
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <div>Loading...</div>}
    </AuthContext.Provider>
  );
};

export default AuthContext; 